import { jsx } from "react/jsx-runtime";
import { useState, useCallback, useRef, useMemo, useEffect, Suspense } from "react";
import { useNavigate, useHref, useLocation, useNavigationType, Route, NavigationType, Routes, useSearchParams } from "react-router-dom";
import { Funnel } from "@airhelp/funnel-core";
import { F as FunnelContextProvider, u as useFunnel } from "./react-D5l6P7RL.js";
function ReactRouterFunnel({
  definition,
  loadingIndicator,
  notFoundStep
}) {
  const navigate = useNavigate();
  const basename = useHref("/");
  const basePathPrefix = basename.endsWith("/") ? basename.substring(0, -1) : basename;
  const location = useLocation();
  const navigationType = useNavigationType();
  const [didInit, setDidInit] = useState(false);
  const onStepChange = useCallback(
    (step, event) => {
      const stepDefinitionUri = `${definition.basePath || ""}${(step == null ? void 0 : step.uri) || ""}`;
      const stepBrowserUri = `${basePathPrefix}${stepDefinitionUri}`;
      const stepPathname = new URL(stepBrowserUri, window.location.href).pathname;
      const stepParams = new URL(stepDefinitionUri, window.location.href).searchParams;
      const windowParams = new URLSearchParams(window.location.search);
      const stepParamsMatch = Array.from(stepParams.entries()).every(
        ([key, value]) => windowParams.get(key) === value
      );
      if (stepPathname !== window.location.pathname || !stepParamsMatch) {
        event.changedFromDisabledStep ? navigate(stepDefinitionUri, { replace: true }) : navigate(stepDefinitionUri);
      }
      if (definition.onStepChange) {
        definition.onStepChange(step, event);
      }
    },
    [basePathPrefix, definition, navigate]
  );
  const funnelRef = useRef(
    new Funnel({
      ...definition,
      // eslint-disable-next-line @typescript-eslint/restrict-template-expressions -- TODO: validate linting
      basePath: `${basePathPrefix}${definition.basePath}`,
      onStepChange,
      resolveLocation: (
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- TODO: validate linting
        definition.resolveLocation || (() => new URL(window.location.toString()))
      )
    })
  );
  const stepsByUriAndParams = useMemo(() => {
    const record = {};
    definition.steps.list.forEach((step) => {
      const stepUrl = new URL(step.uri ?? "", "http://__internal_domain__.com");
      const stepParams = stepUrl.searchParams;
      if (typeof record[stepUrl.pathname] === "undefined") {
        record[stepUrl.pathname] = [{ step, params: stepParams }];
      } else {
        record[stepUrl.pathname].push({ step, params: stepParams });
      }
    });
    return record;
  }, [definition.steps.list]);
  const routes = useMemo(() => {
    return Object.keys(stepsByUriAndParams).map((path) => {
      if (stepsByUriAndParams[path].length === 1) {
        const { step } = stepsByUriAndParams[path][0];
        return /* @__PURE__ */ jsx(
          Route,
          {
            element: /* @__PURE__ */ jsx(FunnelStep, { onStepEnter: definition.onStepEnter, step }),
            path
          },
          path
        );
      }
      return /* @__PURE__ */ jsx(
        Route,
        {
          element: /* @__PURE__ */ jsx(
            ParametrisedFunnelStep,
            {
              notFoundStep,
              onStepEnter: definition.onStepEnter,
              pathSteps: stepsByUriAndParams[path]
            }
          ),
          path
        },
        path
      );
    });
  }, [definition.onStepEnter, notFoundStep, stepsByUriAndParams]);
  useEffect(() => {
    var _a;
    if (navigationType === NavigationType.Pop || navigationType === NavigationType.Replace) {
      try {
        funnelRef.current.detectBrowserStep();
      } catch (e) {
        const redirectUri = (_a = definition.steps.list.at(0)) == null ? void 0 : _a.uri;
        if (redirectUri) {
          navigate(`${definition.basePath}${redirectUri}`, { replace: true });
        } else {
          throw e;
        }
      }
    }
    if (!didInit && funnelRef.current.currentStep === null) {
      funnelRef.current.detectBrowserStep();
    }
    if (!didInit) {
      setDidInit(true);
    }
  }, [location]);
  return /* @__PURE__ */ jsx(Suspense, { fallback: loadingIndicator, children: /* @__PURE__ */ jsx(FunnelContextProvider, { funnel: funnelRef.current, children: /* @__PURE__ */ jsx(Routes, { children: routes }) }) });
}
function ParametrisedFunnelStep({
  onStepEnter,
  pathSteps,
  notFoundStep
}) {
  const [searchParams] = useSearchParams();
  const matchedStep = useMemo(() => {
    const matched = pathSteps.find((item) => {
      const stepParams = item.params;
      for (const [key, value] of searchParams.entries()) {
        if (stepParams.get(key) !== value) {
          return false;
        }
      }
      return true;
    });
    return (matched == null ? void 0 : matched.step) ?? null;
  }, [searchParams, pathSteps]);
  if (!matchedStep) {
    return notFoundStep;
  }
  return /* @__PURE__ */ jsx(FunnelStep, { onStepEnter, step: matchedStep });
}
function FunnelStep({
  onStepEnter,
  step
}) {
  const funnel = useFunnel();
  useEffect(() => {
    onStepEnter && funnel.currentStep && onStepEnter(funnel.currentStep);
  }, []);
  if (!step.Component) {
    throw new Error(
      `Funnel load error, funnel step should always have a component! Occurred at step: ${step.name}`
    );
  }
  return /* @__PURE__ */ jsx(step.Component, {});
}
function useFunnelInit(builder) {
  const isInitialised = useRef(false);
  const definitionRef = useRef(
    null
  );
  if (!isInitialised.current) {
    const { onInit, ...definition } = builder();
    definitionRef.current = definition;
    onInit == null ? void 0 : onInit();
    isInitialised.current = true;
  }
  return definitionRef.current;
}
export {
  ReactRouterFunnel,
  useFunnelInit
};
//# sourceMappingURL=react-router.js.map
